import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarCheck, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ErrorState, FieldLabel, LoadingState, Modal, PageHeading } from "@/components/app/AppUI";
import { OrigenChip } from "@/components/crm/leads/AccionesLead";
import { FichaLead } from "@/components/crm/leads/FichaLead";
import { useInvalidarLeads, useMoverEtapa } from "@/components/crm/leads/useLeadMutations";
import { leadRepository } from "@/data/repositories";
import {
  ETAPAS,
  ETAPAS_A_MANO,
  ETIQUETA_ETAPA,
  ETIQUETA_RESULTADO,
  ETIQUETA_TIPO,
  diasNaturales,
  fechaBoda,
  haceTiempo,
  origenLead,
  type OrigenLead,
} from "@/domain/leads";
import type { Lead, LeadInteraction, LeadStage } from "@/domain/types";

export const Route = createFileRoute("/_authenticated/app/leads")({
  // `?lead=<id>` abre su ficha: así llegan los avisos push del móvil.
  validateSearch: (search: Record<string, unknown>): { lead?: string } => ({
    lead: typeof search.lead === "string" ? search.lead : undefined,
  }),
  component: LeadsPage,
});

const ORIGENES: readonly OrigenLead[] = [
  "Meta",
  "Instagram",
  "Recomendación",
  "Web",
  "Manual",
  "Presentación",
  "Otro",
];

function LeadsPage() {
  const leads = useQuery({ queryKey: ["leads"], queryFn: () => leadRepository.list() });
  const ultimas = useQuery({
    queryKey: ["lead-last"],
    queryFn: () => leadRepository.lastInteractions(),
  });
  const [busqueda, setBusqueda] = useState("");
  const [origen, setOrigen] = useState<OrigenLead | "">("");
  const { lead: leadDelEnlace } = Route.useSearch();
  const [abierto, setAbierto] = useState<string | null>(leadDelEnlace ?? null);
  const [creando, setCreando] = useState(false);

  const porEtapa = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const visibles = (leads.data ?? []).filter(
      (lead) =>
        (!origen || origenLead(lead.source) === origen) &&
        `${lead.name} ${lead.email ?? ""} ${lead.phone ?? ""} ${lead.location ?? ""}`
          .toLowerCase()
          .includes(texto),
    );
    return new Map(ETAPAS.map((etapa) => [etapa, visibles.filter((l) => l.stage === etapa)]));
  }, [leads.data, busqueda, origen]);

  if (leads.isLoading) return <LoadingState />;
  if (leads.error) return <ErrorState message={leads.error.message} />;

  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Ventas"
        title="Leads"
        description="Cada pareja, en la etapa en la que está. Pulsa una tarjeta para ver su ficha."
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/app/hoy" className="btn-secondary">
              <CalendarCheck className="size-4" /> Hoy
            </Link>
            <button className="btn-primary" onClick={() => setCreando(true)}>
              <Plus className="size-4" /> Nuevo lead
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-60 max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="field pl-10"
            placeholder="Buscar por nombre, teléfono, email o lugar"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <select
          className="field !w-auto"
          aria-label="Origen"
          value={origen}
          onChange={(e) => setOrigen(e.target.value as OrigenLead | "")}
        >
          <option value="">Todos los orígenes</option>
          {ORIGENES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
        <div className="grid auto-cols-[minmax(15.5rem,1fr)] grid-flow-col gap-3">
          {ETAPAS.map((etapa) => (
            <Columna
              key={etapa}
              etapa={etapa}
              leads={porEtapa.get(etapa) ?? []}
              ultimas={ultimas.data}
              alAbrir={setAbierto}
            />
          ))}
        </div>
      </div>

      {abierto && <FichaLead leadId={abierto} alCerrar={() => setAbierto(null)} />}
      {creando && <NuevoLead alCerrar={() => setCreando(false)} alCrear={setAbierto} />}
    </div>
  );
}

function Columna({
  etapa,
  leads,
  ultimas,
  alAbrir,
}: {
  etapa: LeadStage;
  leads: Lead[];
  ultimas?: Map<string, LeadInteraction>;
  alAbrir: (id: string) => void;
}) {
  const cerrada = etapa === "RESERVADO" || etapa === "PERDIDO";
  return (
    <section className={`panel flex min-h-64 flex-col ${cerrada ? "bg-secondary/40" : ""}`}>
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="eyebrow text-foreground">{ETIQUETA_ETAPA[etapa]}</h2>
        <span className="font-mono text-xs text-muted-foreground">{leads.length}</span>
      </header>
      <div className="grid content-start gap-2 p-2">
        {leads.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Nadie en esta etapa.</p>
        ) : (
          leads.map((lead) => (
            <TarjetaLead
              key={lead.id}
              lead={lead}
              ultima={ultimas?.get(lead.id)}
              alAbrir={() => alAbrir(lead.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function textoUltima(ultima: LeadInteraction | undefined): string {
  if (!ultima) return "Sin contacto todavía";
  const cuando = haceTiempo(ultima.created_at);
  const que = ultima.outcome ? ETIQUETA_RESULTADO[ultima.outcome] : ETIQUETA_TIPO[ultima.kind];
  return `${cuando.charAt(0).toUpperCase()}${cuando.slice(1)} · ${que}`;
}

function TarjetaLead({
  lead,
  ultima,
  alAbrir,
}: {
  lead: Lead;
  ultima?: LeadInteraction;
  alAbrir: () => void;
}) {
  const mover = useMoverEtapa();
  const dias = diasNaturales(new Date(lead.stage_changed_at), new Date());

  return (
    <article className="border border-border bg-background transition-colors hover:border-foreground">
      <button type="button" className="block w-full p-3 text-left" onClick={alAbrir}>
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold leading-tight">{lead.name}</p>
          <OrigenChip source={lead.source} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {fechaBoda(lead.event_date)} · {lead.location ?? "Lugar sin definir"}
        </p>
        <p className="mt-2 text-xs">{textoUltima(ultima)}</p>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
          {dias === 0
            ? "Hoy en la etapa"
            : dias === 1
              ? "1 día en la etapa"
              : `${dias} días en la etapa`}
        </p>
      </button>
      <div className="border-t border-border px-3 py-2">
        {lead.stage === "RESERVADO" ? (
          lead.converted_client_id ? (
            <Link
              to="/app/clientes/$id"
              params={{ id: lead.converted_client_id }}
              className="font-mono text-[10px] uppercase tracking-[0.12em] text-primary hover:underline"
            >
              Ver cliente →
            </Link>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              Reservado
            </span>
          )
        ) : (
          <select
            className="w-full bg-transparent font-mono text-[10px] uppercase tracking-[0.12em]"
            aria-label={`Mover a ${lead.name} de etapa`}
            value={lead.stage}
            disabled={mover.isPending}
            onChange={(e) => {
              const stage = e.target.value as LeadStage;
              let lostReason: string | null = null;
              if (stage === "PERDIDO") {
                lostReason = window.prompt("¿Por qué se pierde?", "No le interesa");
                if (lostReason === null) return;
              }
              mover.mutate({ id: lead.id, stage, lostReason });
            }}
          >
            {ETAPAS_A_MANO.map((etapa) => (
              <option key={etapa} value={etapa}>
                {ETIQUETA_ETAPA[etapa]}
              </option>
            ))}
          </select>
        )}
      </div>
    </article>
  );
}

type FormularioLead = {
  name: string;
  phone: string;
  email: string;
  eventDate: string;
  location: string;
  note: string;
};

function NuevoLead({ alCerrar, alCrear }: { alCerrar: () => void; alCrear: (id: string) => void }) {
  const invalidar = useInvalidarLeads();
  const form = useForm<FormularioLead>({
    defaultValues: { name: "", phone: "", email: "", eventDate: "", location: "", note: "" },
  });
  const crear = useMutation({
    mutationFn: async (valores: FormularioLead) => {
      const lead = await leadRepository.crear(valores);
      // La nota de alta va a la cronología, como cualquier otra.
      if (valores.note.trim())
        await leadRepository.registrar({
          leadId: lead.id,
          kind: "NOTE",
          outcome: null,
          body: valores.note,
        });
      return lead;
    },
    onSuccess: async (lead) => {
      toast.success("Lead creado");
      alCerrar();
      alCrear(lead.id);
      await invalidar();
    },
    onError: (error) => toast.error(error.message),
  });
  const errores = form.formState.errors;

  return (
    <Modal
      title="Nuevo lead"
      description="Una pareja que te ha llegado por otro canal."
      onClose={alCerrar}
    >
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={form.handleSubmit((valores) => crear.mutate(valores))}
      >
        <div className="sm:col-span-2">
          <FieldLabel label="Nombre" error={errores.name?.message}>
            <input
              className="field"
              autoFocus
              {...form.register("name", {
                validate: (v) => v.trim().length >= 2 || "Escribe al menos el nombre",
              })}
            />
          </FieldLabel>
        </div>
        <FieldLabel label="Teléfono" error={errores.phone?.message}>
          <input
            className="field"
            type="tel"
            {...form.register("phone", {
              validate: (v, todos) =>
                Boolean(v.trim() || todos.email.trim()) || "Pon un teléfono o un email",
            })}
          />
        </FieldLabel>
        <FieldLabel label="Email">
          <input className="field" type="email" {...form.register("email")} />
        </FieldLabel>
        <FieldLabel label="Fecha de boda">
          <input className="field" type="date" {...form.register("eventDate")} />
        </FieldLabel>
        <FieldLabel label="Lugar">
          <input className="field" {...form.register("location")} />
        </FieldLabel>
        <div className="sm:col-span-2">
          <FieldLabel label="Nota">
            <textarea className="field min-h-24" {...form.register("note")} />
          </FieldLabel>
        </div>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <button type="button" className="btn-quiet" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={crear.isPending}>
            Crear lead
          </button>
        </div>
      </form>
    </Modal>
  );
}
